/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, setToken, setCurrentUser } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Loader2, Lock, Delete, Users } from 'lucide-react';
import { homeFor } from '@/lib/base';
import { toast } from 'sonner';

const PAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export default function Login({ zone = 'admin' }) {
  const invite = zone === 'invite';
  const nav = useNavigate();
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (code) => {
    setLoading(true);
    try {
      const r = await api.post('/auth/login', { pin: code, zone });
      setToken(r.data.token);
      setCurrentUser(r.data.user);
      toast.success(`Bienvenue ${r.data.user.name}`);
      nav(homeFor(r.data.user), { replace: true });
    } catch (e) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPin('');
      toast.error(e?.response?.data?.detail || 'PIN incorrect');
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when 6 digits are entered.
  useEffect(() => {
    if (pin.length === 6 && !loading) submit(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const press = (d) => {
    if (loading) return;
    setPin((prev) => (prev.length >= 6 ? prev : prev + d));
  };
  const backspace = () => { if (!loading) setPin((prev) => prev.slice(0, -1)); };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <Card className="card-flat p-10 max-w-sm w-full" data-testid="admin-login-card">
        <div className="flex flex-col items-center mb-8">
          <div className={`w-12 h-12 rounded-md flex items-center justify-center mb-4 ${invite ? 'bg-amber-500' : 'bg-zinc-950'}`}>
            {invite ? <Users className="h-5 w-5 text-white" /> : <Lock className="h-5 w-5 text-white" />}
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="login-title">{invite ? 'Espace invités & collaborateurs' : 'Connexion'}</h1>
          <p className="text-sm text-zinc-500 mt-1">{invite ? 'Saisissez le PIN choisi lors de votre invitation' : 'Saisissez votre PIN à 6 chiffres'}</p>
        </div>

        {/* PIN dots */}
        <div className={`flex justify-center gap-3 mb-8 transition-transform ${shake ? 'animate-[shake_0.4s_ease-in-out]' : ''}`} data-testid="admin-pin-dots">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`w-3.5 h-3.5 rounded-full transition-all ${i < pin.length ? 'bg-zinc-950 scale-110' : 'bg-zinc-200'}`}
            />
          ))}
        </div>

        {/* Pad */}
        <div className="grid grid-cols-3 gap-2.5">
          {PAD.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => press(d)}
              disabled={loading}
              data-testid={`admin-pin-${d}`}
              className="h-14 rounded-md text-2xl font-semibold bg-white border border-zinc-200 hover:bg-zinc-100 active:scale-95 transition disabled:opacity-50"
            >{d}</button>
          ))}
          <div />
          <button
            type="button"
            onClick={() => press('0')}
            disabled={loading}
            data-testid="admin-pin-0"
            className="h-14 rounded-md text-2xl font-semibold bg-white border border-zinc-200 hover:bg-zinc-100 active:scale-95 transition disabled:opacity-50"
          >0</button>
          <button
            type="button"
            onClick={backspace}
            disabled={loading || !pin.length}
            data-testid="admin-pin-backspace"
            className="h-14 rounded-md bg-white border border-zinc-200 hover:bg-zinc-100 active:scale-95 transition disabled:opacity-30 flex items-center justify-center"
          >
            <Delete className="h-4 w-4" />
          </button>
        </div>

        {loading && (
          <div className="mt-6 flex items-center justify-center text-zinc-400 text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Vérification…
          </div>
        )}

        <p className="text-[10px] text-zinc-400 text-center mt-6">
          {invite ? 'PIN oublié ? Demandez un nouveau lien d\'invitation à l\'administrateur.' : 'PIN oublié ? Contactez votre administrateur.'}
        </p>
      </Card>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
